import styled from '@emotion/styled';
import { faCheck, faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { Button, Tooltip } from 'antd';
import { type FC, useEffect, useRef, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { canUseNativeShare, shareEventUrl } from './shareEvent';

// antd's own copyable button shows "Copied" for three seconds.
const SHARE_FEEDBACK_MS = 3 * OneSecondInMillis;

const ShareButton = styled(Button)`
  flex: none;
  color: #646464;
`;

type Props = {
  /** Absolute link the share sheet or clipboard receives. */
  url: string;
  title: string;
};

/** Hands an event's player link to the platform's share sheet, or the clipboard, from a card. */
export const ShareEventButton: FC<Props> = ({ url, title }) => {
  // antd's copyable button acknowledges a copy with a check and a "Copied" tooltip, then resets.
  const [shared, setShared] = useState(false);
  // A share sheet is not a copy: it gets its own word, and "Copied" only ever follows a copy.
  const shareLabel = shared ? 'Copied' : canUseNativeShare() ? 'Share' : 'Copy';
  const shareFeedbackTimeout = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (shareFeedbackTimeout.current !== null) clearTimeout(shareFeedbackTimeout.current);
    },
    [],
  );
  const share = async () => {
    try {
      const method = await shareEventUrl(url, title);
      trackEvent('event_list_share', { method });
      if (method === 'copy') {
        setShared(true);
        if (shareFeedbackTimeout.current !== null) clearTimeout(shareFeedbackTimeout.current);
        shareFeedbackTimeout.current = window.setTimeout(() => setShared(false), SHARE_FEEDBACK_MS);
      }
    } catch {
      // A dismissed share sheet and a blocked clipboard both land here; nothing was shared, and
      // antd's own copyable stays quiet about a failure too.
    }
  };

  return (
    <Tooltip title={shareLabel}>
      <ShareButton
        type="text"
        size="small"
        aria-label="Share this event"
        icon={<FontAwesomeIcon icon={shared ? faCheck : faShare} />}
        onClick={share}
      />
    </Tooltip>
  );
};
